import React, { FC, useLayoutEffect, useRef } from 'react';
import { Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ProductionOverviewCountMode } from 'models/production-overview';
import OverviewGroupRow, { OverviewCellClickHandler, dayColumnColor } from './overview-group-row.component';
import {
    DAY_CELL_ATTRIBUTE,
    OverviewCalendarGroup,
    getDayDateLabel,
    getDayOfMonthLabel,
    getTodayKey,
    getVisibleMaxCount,
    getWeekdayLabel,
    isMonday,
    isWeekendDay,
} from './production-overview-utils';

/** A move to a new range of the same length; a new id replays the slide (DD-3880). */
export type OverviewSlide = { direction: 'forward' | 'back'; id: number };

const SLIDE_DISTANCE_PX = 32;
const SLIDE_DURATION_MS = 220;

/**
 * Slides the day cells in from the side the new days come from. The Web Animations API
 * replays on every move without remounting rows (which would collapse the groups), and
 * leaves no styles behind. Skipped for reduced motion and where animate() is missing.
 */
const slideDayCells = (table: HTMLTableElement, slide: OverviewSlide) => {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    const offset = slide.direction === 'forward' ? SLIDE_DISTANCE_PX : -SLIDE_DISTANCE_PX;
    table.querySelectorAll<HTMLElement>(`[${DAY_CELL_ATTRIBUTE}]`).forEach((cell) =>
        cell.animate?.(
            [
                { transform: `translateX(${offset}px)`, opacity: 0 },
                { transform: 'none', opacity: 1 },
            ],
            { duration: SLIDE_DURATION_MS, easing: 'ease-out' },
        ),
    );
};

type OverviewCalendarTableProps = {
    groups: OverviewCalendarGroup[];
    days: string[];
    countMode: ProductionOverviewCountMode;
    /** The 30-day view draws smaller text in narrower columns. */
    isCompact: boolean;
    slide: OverviewSlide | null;
    onCellClick: OverviewCellClickHandler;
};

const OverviewCalendarTable: FC<OverviewCalendarTableProps> = ({
    groups,
    days,
    countMode,
    isCompact,
    slide,
    onCellClick,
}: OverviewCalendarTableProps) => {
    const tableRef = useRef<HTMLTableElement>(null);

    // Runs before paint, so the new days never show unmoved for a frame.
    useLayoutEffect(() => {
        if (slide && tableRef.current) slideDayCells(tableRef.current, slide);
    }, [slide?.id]);

    const { t: translate } = useTranslation(['production-overview']);
    const todayKey = getTodayKey();
    const heatMaxCount = getVisibleMaxCount(groups, days, countMode);

    return (
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table ref={tableRef} size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                    <TableRow>
                        <TableCell
                            sx={{
                                minWidth: 140,
                                position: 'sticky',
                                left: 0,
                                backgroundColor: 'background.paper',
                                zIndex: 2,
                            }}
                        >
                            {translate('production-overview:CALENDAR_FIRST_COLUMN')}
                        </TableCell>
                        {days.map((dayKey) => {
                            const isToday = dayKey === todayKey;
                            return (
                                <TableCell
                                    key={dayKey}
                                    {...{ [DAY_CELL_ATTRIBUTE]: true }}
                                    align="center"
                                    sx={{
                                        backgroundColor: (theme) =>
                                            isToday || isWeekendDay(dayKey)
                                                ? dayColumnColor(dayKey)
                                                : theme.palette.background.paper,
                                        minWidth: isCompact ? 36 : 70,
                                        whiteSpace: 'nowrap',
                                        borderLeft: isMonday(dayKey) ? '2px solid #ccc' : undefined,
                                        paddingX: isCompact ? 0.25 : 0.5,
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="div"
                                        color="text.secondary"
                                        fontSize={isCompact ? '0.6rem' : undefined}
                                    >
                                        {getWeekdayLabel(dayKey)}
                                    </Typography>
                                    {/* Production planning's header: the full date in the header font; the
                                        30-day view keeps day/month so the columns stay narrow. */}
                                    <Typography
                                        component="div"
                                        fontSize={isCompact ? '0.7rem' : 'inherit'}
                                        fontWeight="inherit"
                                    >
                                        {isCompact ? getDayOfMonthLabel(dayKey) : getDayDateLabel(dayKey)}
                                    </Typography>
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {groups.map((group) => (
                        <OverviewGroupRow
                            key={group.groupKey}
                            group={group}
                            days={days}
                            countMode={countMode}
                            heatMaxCount={heatMaxCount}
                            isCompact={isCompact}
                            onCellClick={onCellClick}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OverviewCalendarTable;
